// src/components/mdx/client/Tabs.client.tsx

"use client"

import { type ComponentPropsWithoutRef, type ElementRef, forwardRef, type ReactNode, type FC, createContext, useContext } from "react"
import { motion, AnimatePresence } from "motion/react"
import { ErrorBoundary } from "~/components/shared/ErrorBoundary"
import { cn } from "~/lib/utils"
import { 
  Tabs as UiTabs, 
  TabsList as UiTabsList, 
  TabsTrigger as UiTabsTrigger, 
  TabsContent as UiTabsContent 
} from "~/components/ui/tabs"
import { TabsErrorFallback } from "~/components/mdx/utils/component-utils.client"

/**
 * Context for detecting if components are used within a Tabs wrapper.
 * This helps individual components know when to provide their own context.
 */
const TabsContext = createContext<boolean>(false)

// NOTE: there is no client-side "is mounted" SSR gate here. These leaves are
// rendered ONLY on the client, after hydration, by makeClientSSR's HydrationGate
// (the crawlable shadow is the server/no-JS output), so they never SSR and the
// old `if (!isMounted) return <></>` Radix-SSR guard is unnecessary. It was also
// harmful: returning empty for one paint right after the gate swapped in the
// freshly-loaded chunk blanked the island (#372 r3461232324). Radix mounts
// cleanly on a normal client render, so the first paint is the real tabs.

/**
 * Tab data structure for client-side functionality.
 */
export interface TabData {
  value: string
  label: string
  content: ReactNode
  contentClassName?: string
  disabled?: boolean
}

/**
 * Enhanced properties interface for client-side Tabs functionality.
 */
export interface TabsProps {
  tabs?: TabData[]
  defaultValue?: string
  value?: string
  onValueChange?: (value: string) => void
  children?: ReactNode
  className?: string
  listClassName?: string
  orientation?: 'horizontal' | 'vertical'
}

// ===== ANIMATION VARIANTS =====

/**
 * Animation variants for tab container entrance.
 */
const tabsContainerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: "easeOut" as const,
      staggerChildren: 0.1
    }
  }
}

/**
 * Animation variants for tab list entrance.
 */
const tabsListVariants = {
  hidden: { opacity: 0, scale: 0.98 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: "easeOut" as const
    }
  }
}

/**
 * Animation variants for tab content panels with smooth transitions.
 */
const tabContentVariants = {
  hidden: { 
    opacity: 0, 
    x: -10,
    filter: 'blur(4px)'
  },
  visible: { 
    opacity: 1, 
    x: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.4,
      ease: "easeOut" as const
    }
  },
  exit: { 
    opacity: 0, 
    x: 10,
    filter: 'blur(2px)',
    transition: {
      duration: 0.2,
      ease: "easeOut" as const
    }
  }
}

/**
 * Animation variants for tab triggers with hover effects.
 */
const tabTriggerVariants = {
  rest: { scale: 1 },
  hover: { 
    scale: 1.02,
    transition: {
      duration: 0.2,
      ease: "easeOut" as const
    }
  },
  tap: { 
    scale: 0.98,
    transition: {
      duration: 0.1
    }
  }
}

// ===== CLIENT COMPONENTS =====

/**
 * Client-side Tabs component with full interactive functionality and enhanced animations.
 * 
 * Provides complete tab switching functionality with state management, smooth animations,
 * and enhanced user interactions while maintaining identical styling to server fallback.
 * 
 * INTERACTIVE FEATURES:
 * - Full tab state management with controlled and uncontrolled modes
 * - Smooth animations between tab panels with blur effects
 * - Enhanced hover and focus states for accessibility
 * - Keyboard navigation support for all tab interactions
 * - Flexible data source handling (tabs prop or composition)
 * 
 * ANIMATION ENHANCEMENTS:
 * - Entrance animations for tab container and list
 * - Smooth panel transitions with AnimatePresence
 * - Hover effects on tab triggers with scale transforms
 * - Blur-to-focus transitions for visual polish
 * - Staggered content reveals for professional feel
 * 
 * @param {object} props - Component properties
 * @param {TabData[]} [props.tabs] - Tab configuration data
 * @param {string} [props.defaultValue] - Default active tab
 * @param {string} [props.value] - Controlled active tab value
 * @param {function} [props.onValueChange] - Tab change callback
 * @param {ReactNode} [props.children] - Composition approach content
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.listClassName] - Tab list styling
 * @param {'horizontal' | 'vertical'} [props.orientation='horizontal'] - Layout direction
 * @returns {JSX.Element} Interactive tabs with animations and state management
 */
export function Tabs({
  tabs = [],
  defaultValue,
  value,
  onValueChange,
  children,
  className,
  listClassName,
  orientation = 'horizontal'
}: TabsProps): JSX.Element {

  // Handle tabs data approach
  if (tabs.length > 0) {
    return (
      <TabsContext.Provider value={true}>
        <motion.div 
          variants={tabsContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="my-8 w-full flex justify-center"
        >
          <div className="w-full max-w-3xl">
            <UiTabs 
              defaultValue={defaultValue ?? tabs[0]?.value}
              value={value}
              onValueChange={onValueChange}
              orientation={orientation}
              className={className}
            >
              <motion.div variants={tabsListVariants}>
                <TabsList className={listClassName}>
                  {tabs.map((tab) => (
                    <TabsTrigger 
                      key={tab.value} 
                      value={tab.value}
                      disabled={tab.disabled}
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </motion.div>
              
              <AnimatePresence mode="wait">
                {tabs.map((tab) => (
                  <motion.div key={tab.value}>
                    <TabsContent 
                      value={tab.value}
                      className={tab.contentClassName}
                    >
                      {tab.content}
                    </TabsContent>
                  </motion.div>
                ))}
              </AnimatePresence>
            </UiTabs>
          </div>
        </motion.div>
      </TabsContext.Provider>
    )
  }

  // Handle composition approach
  return (
    <TabsContext.Provider value={true}>
      <motion.div 
        variants={tabsContainerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="my-8 w-full flex justify-center"
      >
        <div className="w-full max-w-3xl">
          <UiTabs 
            defaultValue={defaultValue}
            value={value}
            onValueChange={onValueChange}
            orientation={orientation}
            className={className}
          >
            {children}
          </UiTabs>
        </div>
      </motion.div>
    </TabsContext.Provider>
  )
}

/**
 * Client-side TabsList component with enhanced styling and animations.
 * 
 * CONTEXT-AWARE RENDERING:
 * When used without a Tabs wrapper (as in MDX composition), this component
 * automatically wraps itself in a minimal Tabs context to prevent context errors.
 * When used within a Tabs wrapper, it renders normally.
 * 
 * @param {object} props - Component properties
 * @param {ReactNode} props.children - Tab trigger elements
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Animated and interactive tab list
 */
export const TabsList = forwardRef<
  ElementRef<typeof UiTabsList>,
  ComponentPropsWithoutRef<typeof UiTabsList>
>(({ className, ...props }, ref) => {
  // Check if we're within a Tabs context
  const tabsContext = useContext(TabsContext)

  // If not within a Tabs context, wrap in a minimal Tabs provider
  if (!tabsContext) {
    return (
      <UiTabs defaultValue="tab1">
        <motion.div 
          variants={tabsListVariants}
          className="flex justify-center"
        >
          <UiTabsList 
            ref={ref} 
            className={cn("inline-flex", className)} 
            {...props} 
          />
        </motion.div>
      </UiTabs>
    )
  }
  
  // Normal rendering within Tabs context
  return (
    <motion.div 
      variants={tabsListVariants}
      className="flex justify-center"
    >
      <UiTabsList 
        ref={ref} 
        className={cn("inline-flex", className)} 
        {...props} 
      />
    </motion.div>
  )
})
TabsList.displayName = "TabsList"

/**
 * Client-side TabsTrigger component with enhanced interactions and animations.
 * 
 * INDIVIDUAL COMPONENT USAGE HANDLING:
 * When used without a Tabs wrapper (as in MDX composition), this component
 * automatically wraps itself in a minimal Tabs context to prevent context errors.
 * 
 * @param {object} props - Component properties
 * @param {string} props.value - Tab identifier
 * @param {ReactNode} props.children - Trigger content
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.disabled] - Disabled state
 * @returns {JSX.Element} Interactive tab trigger with animations
 */
export const TabsTrigger = forwardRef<
  ElementRef<typeof UiTabsTrigger>,
  ComponentPropsWithoutRef<typeof UiTabsTrigger>
>(({ className, children, ...props }, ref) => {
  // Check if we're within a Tabs context
  const tabsContext = useContext(TabsContext)

  // If not within a Tabs context, wrap in a minimal Tabs provider
  if (!tabsContext) {
    return (
      <UiTabs defaultValue="tab1">
        <motion.div
          variants={tabTriggerVariants}
          initial="rest"
          whileHover="hover"
          whileTap="tap"
        >
          <UiTabsTrigger 
            ref={ref}
            className={cn("transition-all duration-200", className)}
            {...props}
          >
            {children}
          </UiTabsTrigger>
        </motion.div>
      </UiTabs>
    )
  }
  
  // Normal rendering within Tabs context
  return (
    <motion.div
      variants={tabTriggerVariants}
      initial="rest"
      whileHover="hover"
      whileTap="tap"
    >
      <UiTabsTrigger 
        ref={ref}
        className={cn("transition-all duration-200", className)}
        {...props}
      >
        {children}
      </UiTabsTrigger>
    </motion.div>
  )
})
TabsTrigger.displayName = "TabsTrigger"

/**
 * Client-side TabsContent component with smooth panel transitions.
 * 
 * INDIVIDUAL COMPONENT USAGE HANDLING:
 * When used without a Tabs wrapper (as in MDX composition), this component
 * automatically wraps itself in a minimal Tabs context to prevent context errors.
 * 
 * @param {object} props - Component properties
 * @param {string} props.value - Tab value identifier
 * @param {ReactNode} props.children - Panel content
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Animated tab content panel
 */
export const TabsContent = forwardRef<
  ElementRef<typeof UiTabsContent>,
  ComponentPropsWithoutRef<typeof UiTabsContent>
>(({ className, children, ...props }, ref) => {
  // Check if we're within a Tabs context
  const tabsContext = useContext(TabsContext)

  // If not within a Tabs context, wrap in a minimal Tabs provider
  if (!tabsContext) {
    return (
      <UiTabs defaultValue="tab1">
        <UiTabsContent 
          ref={ref} 
          className={cn("mt-4 p-6 rounded-xl bg-gray-50 dark:bg-gray-800/50", className)} 
          {...props}
        >
          <motion.div
            variants={tabContentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            layout
          >
            {children}
          </motion.div>
        </UiTabsContent>
      </UiTabs>
    )
  }
  
  // Normal rendering within Tabs context
  return (
    <UiTabsContent 
      ref={ref} 
      className={cn("mt-4 p-6 rounded-xl bg-gray-50 dark:bg-gray-800/50", className)} 
      {...props}
    >
      <motion.div
        variants={tabContentVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        layout
      >
        {children}
      </motion.div>
    </UiTabsContent>
  )
})
TabsContent.displayName = "TabsContent"

/**
 * Client-side TabPanels wrapper component.
 * 
 * Simple container that renders tab panels directly with enhanced animations.
 * Maintains compatibility with composition patterns.
 * 
 * @param {object} props - Component properties
 * @param {ReactNode} props.children - Tab panel components
 * @returns {JSX.Element} Tab panels container
 */
export const TabPanels: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, delay: 0.1 }}
    >
      {children}
    </motion.div>
  )
}
TabPanels.displayName = "TabPanels"

/**
 * Client-side SafeTabs component with comprehensive error handling.
 * 
 * Wraps the Tabs component with ErrorBoundary to prevent crashes from
 * interactive state management or animation errors. Provides graceful
 * degradation when errors occur in client-side functionality.
 * 
 * ERROR HANDLING:
 * - Catches JavaScript errors in tab state management
 * - Provides fallback UI when animations fail
 * - Maintains content accessibility even during errors
 * - Logs errors for debugging while showing user-friendly fallbacks
 * 
 * @param {object} props - Component properties extending Tabs props
 * @param {ReactNode} props.children - Tab content to render safely
 * @returns {JSX.Element} Error-protected tabs with enhanced functionality
 */
export const SafeTabs = ({ children, ...props }: ComponentPropsWithoutRef<typeof Tabs>): JSX.Element => (
  <ErrorBoundary fallback={<TabsErrorFallback />}>
    <Tabs {...props}>{children}</Tabs>
  </ErrorBoundary>
)

/* 
CLIENT COMPONENT FEATURES:

INTERACTIVE FUNCTIONALITY:
🎯 Full tab state management with controlled and uncontrolled modes
🎯 Smooth panel switching with AnimatePresence transitions
🎯 Enhanced keyboard navigation and accessibility
🎯 Proper focus management for screen readers
🎯 Event handling for tab change callbacks
🎯 Support for disabled tabs with visual feedback

ANIMATION ENHANCEMENTS:
🎭 Container entrance with staggered children reveals
🎭 Tab list entrance with scale and opacity effects
🎭 Panel transitions with blur-to-focus effects
🎭 Hover animations on tab triggers with scale transforms
🎭 Smooth transitions between active and inactive states
🎭 Layout animations for content changes

ENHANCED INTERACTIONS:
👆 Hover effects with subtle scale transformations
👆 Tap feedback with brief scale reduction
👆 Enhanced visual states for active/inactive tabs
👆 Smooth color transitions for state changes
👆 Interactive feedback for all clickable elements
👆 Touch-optimized interactions for mobile devices

ERROR HANDLING:
🛡️ Comprehensive ErrorBoundary wrapping for safe operation
🛡️ Graceful degradation when JavaScript errors occur
🛡️ Enhanced error logging for development debugging
🛡️ Fallback UI maintains content accessibility
🛡️ Recovery patterns for animation and state failures
🛡️ User-friendly error messages without technical details

ACCESSIBILITY IMPROVEMENTS:
♿ Enhanced keyboard navigation with proper tab order
♿ Screen reader announcements for tab changes
♿ Focus management with visible focus indicators
♿ ARIA attributes for proper semantic relationships
♿ High contrast mode support with appropriate styling
♿ Reduced motion support for users with vestibular disorders

PERFORMANCE OPTIMIZATIONS:
⚡ Efficient re-renders with proper key management
⚡ AnimatePresence optimization for smooth transitions
⚡ Layout animations with minimal reflow
⚡ GPU-accelerated transforms for smooth animations
⚡ Optimized animation timing for best perceived performance
⚡ Memory management for tab content rendering

FLEXIBLE DATA HANDLING:
📊 Support for both tabs prop array and composition patterns
📊 Automatic fallback to first tab when no default specified
📊 Controlled and uncontrolled component patterns
📊 Dynamic tab content with proper React key management
📊 Seamless integration with form state management
📊 Content lazy loading capabilities for large tab sets
*/

// ===== CLIENT COMPONENT EXPORTS FOR SERVER-SIDE DYNAMIC IMPORTS =====

/**
 * Client-side TabsList component exported for server-side dynamic import.
 * This is the component that gets dynamically imported by the server-side TabsList.
 */
export const TabsListClient = TabsList

/**
 * Client-side TabsTrigger component exported for server-side dynamic import.
 * This is the component that gets dynamically imported by the server-side TabsTrigger.
 */
export const TabsTriggerClient = TabsTrigger

/**
 * Client-side TabsContent component exported for server-side dynamic import.
 * This is the component that gets dynamically imported by the server-side TabsContent.
 */
export const TabsContentClient = TabsContent

/**
 * Client-side TabPanels component exported for server-side dynamic import.
 * This is the component that gets dynamically imported by the server-side TabPanels.
 */
export const TabPanelsClient = TabPanels

/**
 * Client-side SafeTabs component exported for server-side dynamic import.
 * This is the component that gets dynamically imported by the server-side SafeTabs.
 */
export const SafeTabsClient = SafeTabs

/**
 * Client-side Tabs component exported for server-side dynamic import.
 * This is the component that gets dynamically imported by the server-side Tabs.
 */
export const TabsClient = Tabs